import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';

import Form from '../../../../../antd/form/Form/Form.jsx';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect.jsx';
import FormNumberField from '../../../../../antd/form/FormNumberField/FormNumberField.jsx';
import { number, required } from '../../../../../antd/form/FormValidation';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../../antd/form/FormButton/FormButton.jsx';
import { CollapseBlock } from '../../../../../common/index.jsx';
import { FoBasicInformation } from '../../../../../common/FriOpen/FoBasicInformation.jsx';
import { useGetActiveStudentsQuery } from '../../../../../../redux/services/ing_projects.jsx';

const IngProjectsActiveStudentsEditForm = ({ onSubmit, initials, form, student, projects, isLoading }) => {
  const { t } = useTranslation();
  const [projectStudyProgramsOptions, setProjectStudyProgramsOptions] = useState([]);

  const activeProjects = projects?.filter((project) => project.active) || [];

  const handleSubmit = (values) => {
    const cleanedValues = {
      ...values,
      new_project_id: values.new_project_id || null,
    };
    onSubmit(cleanedValues);
  };

  // When a project is selected, update the study program field
  const handleProjectChange = (projectId) => {
    if (projectId) {
      // Find the project and get its study programs
      const project = activeProjects.find((p) => p.id === projectId);
      if (project) {
        // Reset the selected study program
        form.setFieldsValue({
          new_study_program: null,
        });

        // If project has study_programs array, create options from it
        if (project.study_programs && Array.isArray(project.study_programs)) {
          const studyProgramsOptions = project.study_programs.map((sp) => ({
            value: sp.id,
            label: sp.name,
          }));
          setProjectStudyProgramsOptions(studyProgramsOptions);
        } else {
          // If no study programs, reset options
          setProjectStudyProgramsOptions([]);
        }
      }
    } else {
      // Reset both fields if project is cleared
      form.setFieldsValue({
        new_study_program: null,
      });
      setProjectStudyProgramsOptions([]);
    }
  };

  const originalProjectInformation = useMemo(
    () => [
      { label: t('Meno'), value: student?.full_name || '–' },
      { label: t('Študijná skupina'), value: student?.study_group || '–' },
      { label: t('Projekt'), value: student?.project_name || '–' },
      { label: t('Študijný program'), value: student?.study_program || '–' },
      { label: t('Rok nástupu'), value: student?.year_of_study || '–' },
    ],
    [student, t],
  );

  const projectOptions = useMemo(
    () =>
      activeProjects?.map((project) => ({
        value: project.id,
        label: project.name + (project.study_program_name ? ` (${project.study_program_name})` : ''),
      })) || [],
    [activeProjects],
  );

  return (
    <Form form={form} onFinish={handleSubmit} initialValues={initials}>
      <CollapseBlock collapsible title={t('Študent a aktuálne priradenie')}>
        <FoBasicInformation dataToRender={originalProjectInformation} />
      </CollapseBlock>

      <CollapseBlock collapsible title={t('Nový projekt')}>
        <FormSelect
          label={t('Nový projekt')}
          name="new_project_id"
          placeholder={t('Vyberte projekt')}
          options={projectOptions}
          onChange={handleProjectChange}
          rules={[required(t)]}
        />

        <FormSelect
          label={t('Študijný program nového projektu')}
          name="new_study_program"
          placeholder={t('Vyberte študijný program nového projektu')}
          options={projectStudyProgramsOptions}
          rules={[required(t)]}
          disabled={!form.getFieldValue('new_project_id') || projectStudyProgramsOptions.length === 0}
        />

        <FormNumberField
          label={t('Rok nástupu')}
          name="year"
          placeholder={t('Rok nástupu')}
          min={2000}
          max={2100}
          precision={0}
          style={{ width: '100%' }}
          rules={[required(t), number(t, 2000, 2100)]}
        />
      </CollapseBlock>

      <FormButtonsContainer>
        <FormButton submitOnClick={true} submitKey={'ip-active-students'} loading={isLoading}>
          {t('Uložiť')}
        </FormButton>
      </FormButtonsContainer>
    </Form>
  );
};

export default IngProjectsActiveStudentsEditForm;
